import { useState, useMemo } from 'react';

/**
 * Searchable and filterable list component for managing permissions, roles, and access
 *
 * @param {Object} props
 * @param {Array} props.itemsWithAccess - Items that have the permission/role
 * @param {Array} props.itemsWithoutAccess - Items that don't have the permission/role
 * @param {string} props.itemType - Type of items (user, role, product)
 * @param {string} props.accessLabel - Label for what access is being managed (e.g., "Permission", "Role")
 * @param {Function} props.onToggleAccess - Callback when toggling access (itemId, currentHasAccess) => void
 * @param {Function} props.renderItemContent - Function to render custom content for each item
 * @param {boolean} props.useStandardSpacing - Apply standard detail view spacing (p-8, max-w-5xl)
 */
export default function SearchableAccessList({
  title,
  subtitle,
  itemsWithAccess = [],
  itemsWithoutAccess = [],
  itemType = 'item',
  accessLabel = 'Access',
  onToggleAccess,
  renderItemContent,
  useStandardSpacing = true
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [filter, setFilter] = useState('all'); // 'all', 'with', 'without'

  const theme = {
    border: 'border-purple-500',
    bg: 'bg-purple-500/10',
    text: 'text-purple-400',
    button: 'bg-purple-600 hover:bg-purple-700',
    badge: 'bg-purple-500/20 text-purple-400'
  };

  // Toggle item expansion
  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Filter and search items
  const filteredItems = useMemo(() => {
    let withAccess = [...itemsWithAccess];
    let withoutAccess = [...itemsWithoutAccess];

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      withAccess = withAccess.filter(item =>
        item.username?.toLowerCase().includes(search) ||
        item.name?.toLowerCase().includes(search) ||
        item.email?.toLowerCase().includes(search) ||
        item.full_name?.toLowerCase().includes(search)
      );
      withoutAccess = withoutAccess.filter(item =>
        item.username?.toLowerCase().includes(search) ||
        item.name?.toLowerCase().includes(search) ||
        item.email?.toLowerCase().includes(search) ||
        item.full_name?.toLowerCase().includes(search)
      );
    }

    // Apply access filter
    if (filter === 'with') {
      withoutAccess = [];
    } else if (filter === 'without') {
      withAccess = [];
    }

    return { withAccess, withoutAccess };
  }, [itemsWithAccess, itemsWithoutAccess, searchTerm, filter]);

  const totalCount = filteredItems.withAccess.length + filteredItems.withoutAccess.length;

  const content = (
    <>
      {/* Search and Filter Bar */}
      <div className="flex-shrink-0 space-y-3 border-b border-gray-700 pb-4">
        {/* Search Input */}
        <div className="relative">
          <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder={`Search ${itemType}s...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === 'all'
                ? theme.button
                : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
            style={filter === 'all' ? { color: 'var(--color-text-on-accent)' } : {}}
          >
            All ({totalCount})
          </button>
          <button
            onClick={() => setFilter('with')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === 'with'
                ? theme.button
                : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
            style={filter === 'with' ? { color: 'var(--color-text-on-accent)' } : {}}
          >
            With {accessLabel} ({filteredItems.withAccess.length})
          </button>
          <button
            onClick={() => setFilter('without')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === 'without'
                ? theme.button
                : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
            style={filter === 'without' ? { color: 'var(--color-text-on-accent)' } : {}}
          >
            Without {accessLabel} ({filteredItems.withoutAccess.length})
          </button>
        </div>
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-y-auto pt-6 space-y-6">
        {/* Items with access */}
        {filteredItems.withAccess.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
              With {accessLabel} ({filteredItems.withAccess.length})
            </h3>
            <div className="space-y-2">
              {filteredItems.withAccess.map(item => (
                <ItemCard
                  key={item.id}
                  item={item}
                  hasAccess={true}
                  isExpanded={expandedItems.has(item.id)}
                  onToggleExpand={() => toggleExpanded(item.id)}
                  onToggleAccess={() => onToggleAccess?.(item.id, true)}
                  renderContent={renderItemContent}
                  theme={theme}
                  accessLabel={accessLabel}
                />
              ))}
            </div>
          </div>
        )}

        {/* Items without access */}
        {filteredItems.withoutAccess.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
              Without {accessLabel} ({filteredItems.withoutAccess.length})
            </h3>
            <div className="space-y-2">
              {filteredItems.withoutAccess.map(item => (
                <ItemCard
                  key={item.id}
                  item={item}
                  hasAccess={false}
                  isExpanded={expandedItems.has(item.id)}
                  onToggleExpand={() => toggleExpanded(item.id)}
                  onToggleAccess={() => onToggleAccess?.(item.id, false)}
                  renderContent={renderItemContent}
                  theme={theme}
                  accessLabel={accessLabel}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {totalCount === 0 && (
          <div className="flex items-center justify-center h-64">
            <p className="text-gray-400">
              {searchTerm ? `No ${itemType}s match your search` : `No ${itemType}s found`}
            </p>
          </div>
        )}
      </div>
    </>
  );

  return useStandardSpacing ? (
    <div className="p-8 max-w-5xl">
      {/* Title Header */}
      {(title || subtitle) && (
        <div className="mb-6">
          {title && <h2 className="text-2xl font-bold text-white">{title}</h2>}
          {subtitle && <p className="text-gray-400 mt-1">{subtitle}</p>}
        </div>
      )}
      <div className="flex flex-col h-full">
        {content}
      </div>
    </div>
  ) : (
    <div className="h-full flex flex-col">
      {(title || subtitle) && (
        <div className="mb-6">
          {title && <h2 className="text-2xl font-bold text-white">{title}</h2>}
          {subtitle && <p className="text-gray-400 mt-1">{subtitle}</p>}
        </div>
      )}
      {content}
    </div>
  );
}

/**
 * AccessToggleButton - Reusable button for Grant/Remove actions
 */
function AccessToggleButton({ onClick, variant, label, title }) {
  const gradientClasses = variant === 'remove'
    ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-lg shadow-red-900/30 hover:shadow-red-900/50'
    : 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-lg shadow-purple-900/30 hover:shadow-purple-900/50';

  return (
    <button
      onClick={onClick}
      className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all duration-200 hover:scale-105 hover:shadow-xl ${gradientClasses}`}
      title={title}
    >
      {label}
    </button>
  );
}

/**
 * Individual item card component
 */
function ItemCard({ item, hasAccess, isExpanded, onToggleExpand, onToggleAccess, renderContent, theme, accessLabel }) {
  return (
    <div className={`bg-gray-800/40 border border-gray-700 rounded-lg transition-all cursor-pointer ${isExpanded ? theme.border : 'hover:border-gray-600'}`}>
      {/* Main card content */}
      <div className="p-4" onClick={onToggleExpand}>
        <div className="flex items-center gap-3">
          {/* Content wrapper with opacity for items without access */}
          <div className={`flex items-center gap-3 flex-1 min-w-0 ${!hasAccess ? 'opacity-60' : ''}`}>
            {/* Expand/Collapse button - LEFT SIDE, VERTICALLY CENTERED */}
            <div className="flex-shrink-0 p-1 text-gray-400" title={isExpanded ? 'Collapse' : 'Expand'}>
              <svg
                className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>

            {/* Item info */}
            <div className="flex-1 min-w-0">
              {renderContent ? renderContent(item, hasAccess) : (
                <div>
                  <h4 className="text-sm font-semibold text-white">
                    {item.username || item.name || item.display_name}
                  </h4>
                  {item.email && (
                    <p className="text-xs text-gray-400 mt-1">{item.email}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Quick toggle button - RIGHT SIDE */}
          <AccessToggleButton
            onClick={(e) => {
              e.stopPropagation();
              onToggleAccess();
            }}
            variant={hasAccess ? 'remove' : 'grant'}
            label={hasAccess ? 'Remove' : 'Grant'}
            title={hasAccess ? `Remove ${accessLabel}` : `Grant ${accessLabel}`}
          />
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-gray-700 p-4 bg-gray-900/50">
          <div className="text-sm text-gray-400">
            {hasAccess ? (
              <div className="space-y-2">
                <p className="font-medium text-white">Has {accessLabel}</p>
                <p>This {item.username ? 'user' : item.name ? 'role or product' : 'item'} currently has this {accessLabel.toLowerCase()}. Click "Remove" to revoke access.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="font-medium text-white">No {accessLabel}</p>
                <p>This {item.username ? 'user' : item.name ? 'role or product' : 'item'} does not have this {accessLabel.toLowerCase()}. Click "Grant" to provide access.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
